import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import {
  useCreateTPAMutation,
  useGetTPAsQuery,
  usePatchTPAByIdMutation,
} from "services/tpa-api.ts";
import type { TPAType } from "types/DataTypes";
import { useConstants } from "utils/hooks";

type TPAInformationFormProps = {
  fileFromServiceId?: string;
};

const TPAInformationForm: React.FC<
  TPAInformationFormProps & { isReadOnly?: boolean }
> = ({ fileFromServiceId, isReadOnly = false }) => {
  const { constants } = useConstants();
  const { fileId: fileIdFromParams } = useParams<{ fileId: string }>();
  const [patchTPAById] = usePatchTPAByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [createTPA] = useCreateTPAMutation();

  const [selectedTPAId, setSelectedTPAId] = useState<string | null>(null);
  const prevSelectedTPAIdRef = useRef<string | null>(null);

  const [tpaOptions, setTPAOptions] = useState<OptionType[]>([]);
  const [tpaSearchTerm, setTPASearchTerm] = useState<string | null>(null);

  const debouncedSetTPASearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setTPASearchTerm(value);
      }, 500),
    [],
  );

  // cancel any pending debounced search on unmount so it can't fire setState
  // after the component is gone
  useEffect(
    () => () => debouncedSetTPASearchTerm.cancel(),
    [debouncedSetTPASearchTerm],
  );

  const fileId = fileFromServiceId ?? fileIdFromParams;

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  const { data: tpas, isFetching: isTPAsFetching } = useGetTPAsQuery(
    { searchTerm: tpaSearchTerm ?? undefined },
    { skip: !tpaSearchTerm },
  );

  useEffect(() => {
    if (tpas) {
      setTPAOptions(
        tpas.results?.map((tpa: TPAType) => ({
          label: tpa.name,
          value: tpa.id.toString(),
        })) ?? [],
      );
    }
  }, [tpas]);

  // keep selectedTPAId in sync with the file, including when the TPA is
  // unassigned (fileData.tpa === null) so the detail fields lock back down
  useEffect(() => {
    setSelectedTPAId(fileData?.tpa?.id?.toString() ?? null);
  }, [fileData]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (!selectedTPAId) return;
      await patchTPAById({
        id: selectedTPAId,
        data: {
          [field]: value,
        },
      });
    },
    [patchTPAById, selectedTPAId],
  );

  const handleSaveFile = useCallback(
    async (field: string, value: string | number | null) => {
      await patchFileById({
        id: fileId,
        data: {
          [field]: value,
        },
      });
    },
    [patchFileById, fileId],
  );

  const handleChangeTPAOnFile = useCallback(
    async (o: OptionType | null) => {
      if (isReadOnly) return;
      await handleSaveFile("tpa_id", o?.value?.toString() ?? null);
      setSelectedTPAId(o?.value?.toString() ?? null);
    },
    [handleSaveFile, isReadOnly],
  );

  const handleCreateAndAssignTPA = useCallback(
    async (tpaName: string) => {
      if (!fileId) return;

      try {
        const result = await createTPA({ data: { name: tpaName } }).unwrap();

        if (result.id) {
          // automatically assign the newly created TPA to the file
          // (routed through handleSaveFile's computed key so the file patch
          // stays consistent with the tpa_id assignment used elsewhere here)
          await handleSaveFile("tpa_id", result.id.toString());
          setSelectedTPAId(result.id.toString());

          // both mutations have resolved, so reset the search state
          // synchronously: clearing the term/options re-renders the Select
          // with no active "Add" option, closing the dropdown naturally
          setTPASearchTerm(null);
          setTPAOptions([]);
          if (document.activeElement) {
            (document.activeElement as HTMLElement).blur();
          }
        }
      } catch (err) {
        console.error("Failed to create and assign TPA:", err);
        KaboodleToaster.error("Failed to create TPA. Please try again.");
      }
    },
    [createTPA, handleSaveFile, fileId],
  );

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!fileData?.tpa) return;
    const currentTPAId = fileData.tpa.id.toString();
    const isSwitchingTPAs = prevSelectedTPAIdRef.current !== currentTPAId;
    prevSelectedTPAIdRef.current = currentTPAId;

    reset(fileData.tpa, {
      keepDirtyValues: !isSwitchingTPAs,
    });
  }, [fileData, reset]);

  const selectedTPAValue = useMemo(
    () =>
      fileData?.tpa
        ? { label: fileData.tpa.name, value: fileData.tpa.id.toString() }
        : null,
    [fileData?.tpa],
  );

  return (
    <Card
      heading="TPA Information"
      classNames={fileFromServiceId ? "future-medical-tpa-information" : ""}
    >
      <Select
        name="tpa"
        label="TPA"
        options={tpaOptions}
        value={selectedTPAValue}
        placeholder="Search for a TPA..."
        readOnly={isReadOnly}
        // preserves the prior behavior: a file always keeps its assigned TPA
        // until a different one is picked (unassigning is not a supported
        // action here); handleChangeTPAOnFile still handles null defensively
        isClearable={false}
        onChange={(o: OptionType | null) => handleChangeTPAOnFile(o)}
        onInputChange={(inputValue: string) =>
          !isReadOnly && debouncedSetTPASearchTerm(inputValue)
        }
        noOptionsMessage={({ inputValue }: { inputValue: string }) => {
          if (!inputValue) return "Begin typing to search for TPAs...";
          if (isTPAsFetching) return "Searching...";

          // only show "Add new item" if we have searched and found no results
          if (inputValue && tpaSearchTerm && tpaOptions.length === 0) {
            return (
              <div className="custom-no-options">
                <div className="no-options-text">No TPAs found.</div>
                <button
                  type="button"
                  className="add-new-item-btn"
                  onClick={() => handleCreateAndAssignTPA(inputValue)}
                >
                  Add {inputValue}
                </button>
              </div>
            );
          }

          return "No TPAs found.";
        }}
      />
      <ControlledInput
        control={control}
        name="name"
        label="TPA Name"
        onSave={(value) => handleSave("name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedTPAId}
      />
      <ControlledInput
        control={control}
        name="address"
        label="TPA Address"
        onSave={(value) => handleSave("address", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedTPAId}
      />
      <ControlledSelect
        control={control}
        label="State"
        name="state"
        options={constants?.STATES || []}
        onSave={(o: OptionType) => handleSave("state", o.value?.toString())}
        errors={errors}
        preventClear={true}
        readOnly={isReadOnly || !selectedTPAId}
      />
      <ControlledInput
        control={control}
        name="zipcode"
        label="Zip Code"
        onSave={(value) => handleSave("zipcode", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="zipcode"
        readOnly={isReadOnly || !selectedTPAId}
      />
    </Card>
  );
};

export default TPAInformationForm;
